/**
 * Response DTOs for the account-scoped Soroush_API dashboard read endpoints, mirroring the
 * shapes produced by `@soroush/services` `routeReadRequest` (see services/src/api/reads.ts).
 *
 * Over the wire the domain's branded identifiers (CheckId, ResultId, ...) are plain strings, so the
 * DTOs here use `string` for ids. The classification/state/location enums are reused from
 * `@soroush/core` (they are plain string-union types) so the SPA and backend cannot drift on the
 * vocabulary. Every DTO is already secret-redacted by the API before serialization (Req 14.4).
 */

import type {
  ApiSpec,
  BrowserSpec,
  CheckState,
  CheckType,
  EntityOrigin,
  FieldError,
  FireOn,
  GlobalLocation,
  IntegrationType,
  Modality,
  RateLimit,
  ResourceProfile,
  ResultStatus,
  ResultTiming,
  Severity,
  UptimeSpec,
  WarmPool,
} from '@soroush/core';

/** A Check as returned by the read API (secret values masked, references preserved). */
export interface CheckDto {
  checkId: string;
  accountId: string;
  projectId: string;
  logicalName: string;
  type: CheckType;
  enabled: boolean;
  intervalSeconds: number;
  locations: GlobalLocation[];
  failureThreshold: number;
  maxRetries: number;
  latencySensitive: boolean;
  /** Type-specific spec; shape depends on `type`. Rendered in detail views (pass B). */
  spec: unknown;
  /**
   * Which write path manages this Check (granular-crud-api Req 6.9). The API applies the
   * read-boundary default, so an entity persisted before the attribute existed reads as
   * `manifest` — the dashboard never has to reproduce that default.
   */
  origin?: EntityOrigin;
  [key: string]: unknown;
}

/**
 * A Check's rolled-up Check_State (Req 12.1). A Check with no recorded results yet reads as the
 * initial fold — `up`, zero failing streak, `hasResults: false`.
 */
export interface CheckStateView {
  state: CheckState;
  consecutiveFailures: number;
  hasResults: boolean;
  latestStatus?: ResultStatus;
  updatedAt?: string;
}

/** One row of `GET /v1/checks`: a Check plus its current Check_State. */
export interface CheckWithState {
  check: CheckDto;
  state: CheckStateView;
}

/** `GET /v1/checks` — the account's Checks with current state. */
export interface ListChecksResponse {
  checks: CheckWithState[];
}

/** `GET /v1/checks/{id}` — one Check with its current state. */
export interface GetCheckResponse {
  check: CheckDto;
  state: CheckStateView;
}

/** A single Check result (Req 12.3). A trace link is present exactly when the result has a trace. */
export interface ResultDto {
  resultId: string;
  checkId: string;
  accountId: string;
  location: GlobalLocation;
  status: ResultStatus;
  timestamp: string;
  durationMs: number;
  timing: ResultTiming;
  /** `/v1/traces/{traceId}` when the result has an associated trace, else `null` (Req 11.3). */
  traceLink: string | null;
  httpStatus?: number;
  failureReason?: string;
  failingStep?: number;
  failingAssertion?: number;
  screenshotS3Key?: string;
  traceId?: string;
}

/** `GET /v1/checks/{id}/results?from&to` — a Check's results over a time range. */
export interface ListResultsResponse {
  checkId: string;
  from: string | null;
  to: string | null;
  results: ResultDto[];
}

/** `GET /v1/results/{resultId}` — a single result's detail. */
export interface GetResultResponse {
  result: ResultDto;
}

/** A trace index entry (Req 11.3). */
export interface TraceDto {
  traceId: string;
  resultId: string;
  checkId: string;
  accountId: string;
  spansS3Key: string;
}

/** `GET /v1/traces/{traceId}` — a trace index entry. */
export interface GetTraceResponse {
  trace: TraceDto;
}

/** Inclusive time range for a results query. Values are epoch seconds or ISO-8601 strings. */
export interface ResultRangeQuery {
  from?: string;
  to?: string;
}

/* -------------------------------------------------------------------------- */
/* Alert_Rule read DTOs (granular-crud-api Req 4.1, 4.2)                       */
/* -------------------------------------------------------------------------- */

/**
 * How an Alert_Rule selects the Checks it applies to, as it appears on the wire — structurally the
 * core `CheckSelector` with the branded `CheckId`s widened to plain strings.
 */
export type CheckSelectorDto =
  | { kind: 'all' }
  | { kind: 'logicalName'; names: string[] }
  | { kind: 'checkId'; checkIds: string[] };

/** An Alert_Rule as returned by the read and write APIs (granular-crud-api Req 4.2, 6.9). */
export interface AlertRuleDto {
  ruleId: string;
  accountId: string;
  projectId: string;
  checkSelector: CheckSelectorDto;
  fireOn: FireOn;
  severity: Severity;
  maxNotificationRate: RateLimit;
  channels: string[];
  integrations: IntegrationType[];
  consecutiveFailures?: number;
  groupingKey?: string;
  /** Which write path manages this rule; defaulted to `manifest` server-side (Req 6.8, 6.9). */
  origin?: EntityOrigin;
  [key: string]: unknown;
}

/** `GET /v1/alert-rules` — the account's Alert_Rules. */
export interface ListAlertRulesResponse {
  alertRules: AlertRuleDto[];
}

/** `GET /v1/alert-rules/{id}` — one Alert_Rule's complete configuration. */
export interface GetAlertRuleResponse {
  alertRule: AlertRuleDto;
}

/* -------------------------------------------------------------------------- */
/* Write payloads (granular-crud-api Design: Data Models / Write payloads)      */
/* -------------------------------------------------------------------------- */

/** The type-specific spec of a Check, per its `type` discriminant. */
export type CheckSpecPayload = UptimeSpec | ApiSpec | BrowserSpec;

/**
 * `POST /v1/checks` body. The server owns `checkId`, `accountId`, and `origin` — supplying
 * `origin` is rejected — and defaults `enabled`, the thresholds, and `resourceProfile`.
 */
export interface CreateCheckPayload {
  projectId: string;
  logicalName: string;
  type: CheckType;
  intervalSeconds: number;
  locations: GlobalLocation[];
  spec: CheckSpecPayload;
  enabled?: boolean;
  failureThreshold?: number;
  maxRetries?: number;
  latencySensitive?: boolean;
  secretsRefs?: string[];
  resourceProfile?: Partial<ResourceProfile>;
  warmPool?: WarmPool;
}

/**
 * `PATCH /v1/checks/{id}` body: a top-level merge patch over the mutable fields. `projectId` (with
 * the identifiers and `origin`) is immutable and rejected if present; `null` clears an optional
 * field (`warmPool`).
 */
export type CheckPatch = {
  [K in keyof Omit<CreateCheckPayload, 'projectId'>]?: CreateCheckPayload[K] | null;
};

/** `POST /v1/alert-rules` body. The server owns `ruleId`, `accountId`, and `origin`. */
export interface CreateAlertRulePayload {
  projectId: string;
  checkSelector: CheckSelectorDto;
  fireOn: FireOn;
  severity: Severity;
  maxNotificationRate: RateLimit;
  channels?: string[];
  integrations?: IntegrationType[];
  consecutiveFailures?: number;
  groupingKey?: string;
}

/**
 * `PATCH /v1/alert-rules/{id}` body: a top-level merge patch. `null` clears an optional field
 * (`consecutiveFailures`, `groupingKey`).
 */
export type AlertRulePatch = {
  [K in keyof Omit<CreateAlertRulePayload, 'projectId'>]?: CreateAlertRulePayload[K] | null;
};

/* -------------------------------------------------------------------------- */
/* Write response envelopes (granular-crud-api Design: Response envelopes)     */
/* -------------------------------------------------------------------------- */

/** `POST /v1/checks` — `201 { check }` with the complete created entity. */
export interface CreateCheckResponse {
  check: CheckDto;
}

/**
 * `PATCH /v1/checks/{id}` — `200 { check, adopted?: true, previousOrigin? }`.
 *
 * The two optional fields are present **exactly when the write transferred ownership** — the Origin
 * the server stamped differs from the stored one (Ownership_Transfer; terraform-provider Req 5.8) —
 * and an ordinary update omits both. They arrive together or not at all, so the dashboard is never
 * told that management moved without being told what it moved from.
 *
 * `previousOrigin` is the Origin the entity had **before** the write, with the read-boundary default
 * applied; the new one is already on `check.origin`. That is what lets the page distinguish the
 * Phase 2a manifest Adoption from the Requirement 13.5 case it must confirm differently — a
 * `previousOrigin: 'terraform'` means the next `terraform plan` will show this change.
 *
 * Additive JSON: `adopted` alone is what granular-crud-api shipped (Req 6.6), so a page that reads
 * only `adopted` keeps working.
 */
export interface UpdateCheckResponse {
  check: CheckDto;
  adopted?: true;
  previousOrigin?: EntityOrigin;
}

/** `DELETE /v1/checks/{id}` — `200 { deleted: true, id }`. */
export interface DeleteCheckResponse {
  deleted: true;
  id: string;
}

/** `POST /v1/alert-rules` — `201 { alertRule }`. */
export interface CreateAlertRuleResponse {
  alertRule: AlertRuleDto;
}

/**
 * `PATCH /v1/alert-rules/{id}` — `200 { alertRule, adopted?: true, previousOrigin? }`. The two
 * optional fields carry exactly the meaning {@link UpdateCheckResponse} documents: present together
 * on an Ownership_Transfer, absent together otherwise (terraform-provider Req 5.8).
 */
export interface UpdateAlertRuleResponse {
  alertRule: AlertRuleDto;
  adopted?: true;
  previousOrigin?: EntityOrigin;
}

/**
 * `DELETE /v1/alert-rules/{id}` — `200 { deleted: true, id, resolvedAlerts }`, where
 * `resolvedAlerts` counts the firing Alerts the deletion closed (Req 5.5).
 */
export interface DeleteAlertRuleResponse {
  deleted: true;
  id: string;
  resolvedAlerts: number;
}

/* -------------------------------------------------------------------------- */
/* Generation plane — Source_Document, Generation_Request, Draft_Check         */
/* (Feature: ai-check-generation, task 23.1; Requirements 9.1, 9.3, 9.4, 9.5,  */
/*  9.8; Design: §Components 11, §Wire shapes)                                 */
/* -------------------------------------------------------------------------- */

/**
 * How a Source_Document reached the platform, and where it now lives — a **reference, never
 * content**, mirroring `services/src/generation/types.ts` `SourceDocumentRef`.
 *
 * The `url` arm's `documentKey` / `contentType` / `byteLength` are absent until the worker's fetch
 * completes, and stay absent on a fetch that was refused or failed; the `upload` arm carries its
 * three unconditionally because there the object already exists. The `pasted` arm stores no
 * document, so it records only the character count.
 */
export type SourceDocumentRefDto =
  | { mode: 'upload'; documentKey: string; contentType: string; byteLength: number }
  | { mode: 'url'; url: string; documentKey?: string; contentType?: string; byteLength?: number }
  | { mode: 'pasted'; characterCount: number };

/**
 * Who acted on a Generation_Request or a Draft_Check. The `apiKey` arm carries the public `keyId`,
 * never an API_Key_Value (services Req 16.2, 16.4).
 */
export type ActorRefDto = { kind: 'user'; userId: string } | { kind: 'apiKey'; keyId: string };

/**
 * A non-fatal note the reviewing human should see: the API replaced a literal credential in a
 * credential-bearing field with a secret reference before persisting (services Req 15.6). The
 * literal itself is deliberately not recorded.
 */
export interface DraftWarningDto {
  kind: 'literal_credential_replaced';
  /** Dotted path of the field whose literal value was replaced. */
  path: string;
  /** The reference substituted in its place, as `${secret:<ref>}` in the configuration. */
  secretRef: string;
}

/** Exactly the three Draft_Check states the API exposes (services Req 7.2). */
export type DraftCheckState = 'pending' | 'accepted' | 'discarded';

/**
 * Where one Generation_Model invocation actually executed — the mirror of
 * `services/src/generation/types.ts`'s `InferenceExecutionContext`
 * (Feature: customer-managed-inference, task 13.2; Requirements 11.1, 11.3, 11.4, 11.5).
 *
 * Four facts, and the answer to "where did this draft come from" months later. In `customer_hosted`
 * mode {@link accountId} is the account the **assumed-role ARN STS returned** named, not the account in
 * the configured role ARN (Requirement 11.3); in `soroush_hosted` mode it is Soroush's own deployment
 * account and region (Requirement 11.4).
 *
 * **It carries no credential material, no External_Id, and no role ARN**, and that is the server-side
 * type's shape rather than a projection this package applies: an account identifier is not a secret
 * while a session token is (Requirement 11.7). {@link inferenceProfileId} duplicates the attempt's own
 * field on purpose, so the block is a complete answer wherever it is rendered on its own.
 */
export interface InferenceExecutionContextDto {
  /** Which mode ran this attempt (Requirement 11.1); equal on every attempt of one request. */
  mode: InferenceMode;
  /** The AWS account the invocation executed in (Requirements 11.3, 11.4). */
  accountId: string;
  /** The region the invocation executed in (Requirement 11.1). */
  region: string;
  /** The Inference_Profile this attempt targeted. */
  inferenceProfileId: string;
}

/**
 * A Draft_Check as returned by `GET /v1/check-drafts`, `GET /v1/check-drafts/{id}`,
 * `PATCH /v1/check-drafts/{id}`, and the accept route — the shape `toCheckDraftDto` produces
 * (services/src/api/entity-dto.ts), already secret-redacted.
 *
 * A draft is a Check *payload*, not a Check: `configuration` is an untyped record because it has
 * passed only the pure Tier-A validation and may be persisted with `validationErrors` outstanding
 * (services Req 5.5, 7.4), which is what makes the review form editable rather than terminal.
 */
export interface CheckDraftDto {
  draftId: string;
  accountId: string;
  generationRequestId: string;
  state: DraftCheckState;
  /** The generated Check payload; `type` is restricted to `uptime` or `api`. */
  configuration: Record<string, unknown>;
  /**
   * Errors still outstanding, present only when the correction budget was exhausted with errors
   * remaining. Paths are rooted at `check.` (`DRAFT_PATH_ROOT`), except the synthetic
   * `generation.output` error a model that returned nothing schema-conforming produces.
   */
  validationErrors?: FieldError[];
  warnings?: DraftWarningDto[];
  /** Where the draft came from — mode and reference, never content (Req 9.3). */
  sourceDocument: SourceDocumentRefDto;
  /** The Inference_Profile that produced this draft, shown on the review page (Req 9.3). */
  inferenceProfileId: string;
  /**
   * Where the attempt that produced this draft actually executed — mode, AWS account, region, and
   * profile (customer-managed-inference Requirements 11.5, 11.6).
   *
   * **Optional, and the absences are real.** It is a *join* rather than a stored field: the server
   * reads the originating Generation_Request and takes the context off the attempt whose
   * `inferenceProfileId` matches the draft's, so it is absent on a draft whose request has aged out of
   * the audit window, on a draft written before this feature existed, and on the `GET /v1/check-drafts`
   * **list**, which does not join it at all (decorating N drafts would cost N lookups). The review page
   * says so rather than guessing — a made-up account and region would be worse than an absent one.
   */
  inference?: InferenceExecutionContextDto;
  correctionAttempts: number;
  createdAtEpoch: number;
  /** Creation + the configured draft lifetime; the expiry timestamp the drafts list shows (Req 9.8). */
  expiresAtEpoch: number;
  acceptedAtEpoch?: number;
  acceptedBy?: ActorRefDto;
  /** The Check acceptance created; its presence is what makes a repeated acceptance idempotent. */
  createdCheckId?: string;
  discardedAtEpoch?: number;
  discardedBy?: ActorRefDto;
}

/**
 * The Generation_Request state machine. `succeeded`, `failed`, and `timed_out` are the three
 * terminal states — the ones {@link TERMINAL_GENERATION_REQUEST_STATES} names and at which the
 * polling hook stops (Req 9.2).
 */
export type GenerationRequestState =
  | 'queued'
  | 'fetching'
  | 'extracting'
  | 'generating'
  | 'validating'
  | 'succeeded'
  | 'failed'
  | 'timed_out';

/**
 * The three states at which a Generation_Request is done. Declared here, beside the state union, so
 * the polling hook (task 23.2) and the generate page agree on terminality without either spelling
 * the three literals out.
 */
export const TERMINAL_GENERATION_REQUEST_STATES: readonly GenerationRequestState[] = [
  'succeeded',
  'failed',
  'timed_out',
];

/** One Generation_Model invocation and what it cost (services Req 4.10, 14.7, 16.2). */
export interface GenerationAttemptDto {
  /** 1-based attempt number; `attempt > 1` implies the escalation tier. */
  attempt: number;
  inferenceProfileId: string;
  inputTokens: number;
  outputTokens: number;
  outcome: 'valid' | 'invalid' | 'error';
  validationErrorCount?: number;
  /**
   * Where this attempt executed (customer-managed-inference Requirements 11.1–11.4).
   *
   * **Optional here even though every attempt this platform writes has one.** The services-side type
   * declares it required, but stored items are read back through an unvalidated cast and audit records
   * live thirteen months, so an attempt persisted before this feature projects with no `inference` key
   * at all. Nothing fabricates one.
   */
  inference?: InferenceExecutionContextDto;
}

/**
 * Why a Generation_Request ended without an acceptable Draft_Check — the discriminants of the
 * services-side `GenerationFailure` union, which is closed over the design's asynchronous error
 * table. This is what Requirement 9.6's "display the returned error" renders.
 */
export type GenerationFailureKind =
  | 'fetch_rejected'
  | 'fetch_redirect_limit'
  | 'fetch_too_large'
  | 'fetch_timeout'
  | 'unsupported_content_type'
  | 'extraction_timeout'
  | 'no_text'
  | 'input_too_large'
  | 'profile_unavailable'
  | 'validation_failed'
  | 'model_throttled'
  | 'model_error'
  | 'generation_timeout'
  /**
   * Resolution never reached a model: the Account is `customer_hosted` and its configuration could not
   * be used (customer-managed-inference Requirement 7.4). **One arm, not eleven** — the nested
   * `InferenceFailure` keeps its own eleven-member `kind`, and the same value is hoisted onto
   * {@link GenerationRequestDto.inferenceFailure} so a reader need not unwrap the outcome union.
   */
  | 'inference_failed';

/**
 * A typed generation failure: the discriminant plus that arm's own datum — the effective token
 * limit, the supported content types, the configured timeout, and so on.
 *
 * The per-arm payload is read through the index signature rather than mirrored as a thirteen-arm
 * union, following the same choice `CheckDto.spec` makes: the discriminant is the stable, useful
 * part (a renderer switches on `kind`), while duplicating every arm's fields across the package
 * boundary would be a second copy of a services-side type to keep in step. No arm carries free
 * text, so nothing here can echo document content.
 */
export interface GenerationFailureDto {
  kind: GenerationFailureKind;
  [key: string]: unknown;
}

/** A Generation_Request's terminal disposition; absent while the request is non-terminal. */
export type GenerationOutcomeDto =
  | { kind: 'succeeded' }
  | { kind: 'failed'; failure: GenerationFailureDto };

/**
 * A Generation_Request as returned by the read routes — the shape `toGenerationRequestDto`
 * produces. One item serves both the progress read (Req 9.2) and the audit record.
 */
export interface GenerationRequestDto {
  generationRequestId: string;
  accountId: string;
  actor: ActorRefDto;
  state: GenerationRequestState;
  /** The submitted Source_Document; retained across a retry so no re-upload is needed (Req 9.6). */
  sourceDocument: SourceDocumentRefDto;
  guidance: {
    projectId: string;
    locations: string[];
    logicalName?: string;
    hint?: string;
  };
  extractedCharacterCount?: number;
  estimatedInputTokens?: number;
  attempts: GenerationAttemptDto[];
  correctionAttempts: number;
  /**
   * Which account processed this request (customer-managed-inference Requirements 11.1, 13.8, 17.1).
   *
   * **Optional for the same reason {@link CheckDto.origin} is**: the server always sends it, applying a
   * read-boundary default so a Generation_Request written before this feature reads as
   * `soroush_hosted` rather than omitting the field — but this mirror describes what a dashboard may
   * *receive*, and it is the mirror's job to make a reader handle the absence rather than assume it
   * away. `CheckGeneratePage` treats an absent value exactly as it treats no history at all: it states
   * the platform default with an explicit hedge instead of asserting a mode it was not told.
   */
  inferenceMode?: InferenceMode;
  /**
   * Why resolution never reached a model (customer-managed-inference Requirements 7.4, 11.7).
   *
   * Present exactly when {@link outcome} is a failure of kind `inference_failed`, carrying the same
   * value — hoisted onto the record so a surface can state what the customer must fix in their own
   * account without unwrapping the outcome union. Carries an exception **class** at most, never a
   * provider message, and never the External_Id (Requirements 7.6, 7.9).
   */
  inferenceFailure?: InferenceFailureDto;
  /** The Draft_Check produced, on a succeeded request and on a validation-exhausted one. */
  draftId?: string;
  outcome?: GenerationOutcomeDto;
  createdAtEpoch: number;
  startedAtEpoch?: number;
  completedAtEpoch?: number;
  acceptance?: { atEpoch: number; actor: ActorRefDto; checkId: string };
  discard?: { atEpoch: number; actor: ActorRefDto };
}

/* -------------------------------------------------------------------------- */
/* Generation plane — request payloads and response envelopes                  */
/* -------------------------------------------------------------------------- */

/** `POST /v1/source-documents/upload-url` body (services Req 1.3, 1.4, 1.5). */
export interface CreateSourceDocumentUploadUrlPayload {
  /** Declared media type; must be in the API's upload allowlist or the request is a `400`. */
  contentType: string;
  /** Exact byte length of the object to be uploaded; it is a *signed* value, so it must be honest. */
  contentLength: number;
  /** Original filename, recorded for diagnostics only — it never influences the minted key. */
  filename?: string;
}

/**
 * `POST /v1/source-documents/upload-url` — `201 { documentKey, uploadUrl, expiresAt, requiredHeaders }`.
 *
 * Two fields must be **echoed, not recomputed**:
 *
 *  - `documentKey` is platform-minted inside the credential's own Account prefix. A key the client
 *    composed is refused by the submit route (services Req 1.7, 15.9), so
 *    {@link CreateGenerationRequestPayload} takes this value verbatim.
 *  - `requiredHeaders` are the *signed* `content-type` and `content-length`. S3 compares them byte
 *    for byte, so a PUT that recomputes either gets an opaque `403` with nothing to say which was
 *    wrong. {@link import('./client').uploadSourceDocument} sends exactly this map.
 */
export interface CreateSourceDocumentUploadUrlResponse {
  documentKey: string;
  uploadUrl: string;
  /** Presigned-URL expiry in epoch **seconds**. */
  expiresAt: number;
  /** The exact headers the presigned PUT must carry. */
  requiredHeaders: Record<string, string>;
}

/**
 * The one Source_Document a generation request carries (services Req 1.1, 1.2).
 *
 * The `?: never` arms are the point: exactly one mode is accepted, and a caller that supplies two
 * does not typecheck — so Requirement 1.2's rejection is unreachable from the dashboard rather than
 * merely avoided by it, the same way the CLI's three-arm `GenerateSource` union is.
 */
export type GenerationSourcePayload =
  | { documentKey: string; url?: never; text?: never }
  | { url: string; documentKey?: never; text?: never }
  | { text: string; documentKey?: never; url?: never };

/**
 * The first-party guidance a submission may supply. Every field is optional; the API applies the
 * deployment's configured defaults for `projectId` and `locations` when they are absent.
 *
 * `locations` is **not** in the design's wire shape, which lists `{ projectId, logicalName, hint }`,
 * but the shipped submit route accepts it (`readLocations` in `services/src/api/generation-submit.ts`)
 * because a Check cannot exist without locations and an operator who cares should be able to say so
 * in the same request.
 */
export interface GenerationGuidancePayload {
  projectId?: string;
  locations?: string[];
  logicalName?: string;
  /** Free-form operator guidance; trimmed and length-bounded server-side before it is stored. */
  hint?: string;
}

/** `POST /v1/generation-requests` body: exactly one source mode plus optional guidance. */
export type CreateGenerationRequestPayload = GenerationSourcePayload & GenerationGuidancePayload;

/**
 * `POST /v1/generation-requests` — `202 { generationRequestId, state: 'queued', inferenceMode }`.
 *
 * `inferenceMode` is the **resolved** mode the platform stamped on the Generation_Request it just
 * created, and it is the half of Requirement 13.8 no read model can cover: on an Account's very first
 * generation there is no prior request to read a mode from, so this is what makes the post-submit
 * confirmation exact. Additive on a route that already existed — the design's
 * `202 { generationRequestId, state }` is intact beside it (Requirement 17.6), which is also why it is
 * declared optional here rather than required.
 */
export interface CreateGenerationRequestResponse {
  generationRequestId: string;
  state: 'queued';
  inferenceMode?: InferenceMode;
}

/** `GET /v1/generation-requests` — the Account's requests in chronological order. */
export interface ListGenerationRequestsResponse {
  generationRequests: GenerationRequestDto[];
}

/**
 * `GET /v1/generation-requests/{id}` — `200 { generationRequest, draft? }`. The draft is present
 * once the request reaches `succeeded` or `failed` and the draft has not yet aged out; `timed_out`
 * never carries one, because nothing was persisted (services Req 6.2).
 */
export interface GetGenerationRequestResponse {
  generationRequest: GenerationRequestDto;
  draft?: CheckDraftDto;
}

/** `GET /v1/check-drafts` — every draft of the Account, in every state (Req 9.8). */
export interface ListCheckDraftsResponse {
  checkDrafts: CheckDraftDto[];
}

/** `GET /v1/check-drafts/{id}` — one draft's complete configuration and outstanding errors. */
export interface GetCheckDraftResponse {
  checkDraft: CheckDraftDto;
}

/**
 * `PATCH /v1/check-drafts/{id}` body: a top-level merge patch over the draft's **Check
 * configuration**, not over the draft record.
 *
 * Deliberately structural rather than {@link CheckPatch}: the draft configuration is an untyped
 * record that may be incomplete, and the API projects the merged result onto the Draft_Check schema
 * whitelist before validating it — a field the schema does not declare is dropped rather than
 * rejected. `draftId`, `accountId`, `state`, and `generationRequestId` are immutable and rejected if
 * present; `null` clears `enabled`, `failureThreshold`, `maxRetries`, or `secretsRefs`.
 */
export type CheckDraftPatch = Record<string, unknown>;

/** `PATCH /v1/check-drafts/{id}` — `200 { checkDraft }` with the complete updated draft. */
export interface PatchCheckDraftResponse {
  checkDraft: CheckDraftDto;
}

/**
 * `POST /v1/check-drafts/{id}/accept` — `201 { check, checkDraft }`, or `200` with the same shape on
 * a replayed acceptance (services Req 6.4, 6.6). This is the **only** route that creates a Check
 * from a draft (Req 9.4).
 */
export interface AcceptCheckDraftResponse {
  check: CheckDto;
  checkDraft: CheckDraftDto;
}

/**
 * `DELETE /v1/check-drafts/{id}` — `200 { discarded: true, id }`. The draft is *marked* discarded,
 * not deleted, so it stays readable and listable until it expires.
 */
export interface DiscardCheckDraftResponse {
  discarded: true;
  id: string;
}

/* -------------------------------------------------------------------------- */
/* Membership plane — Account members and invitations                          */
/* (Feature: user-and-data-management; Requirements 5, 6, 7; Design: §11)      */
/* -------------------------------------------------------------------------- */

/**
 * A member's role in one Account — exactly one per membership (Req 5.1), and the same three values
 * the API's authorization matrix already grades every other route against.
 *
 * Declared here rather than imported from `@soroush/core` for the reason
 * {@link CHECK_PAYLOAD_DEFAULTS} spells out: the SPA bundle takes **types** from the backend
 * packages and never values, and this union is needed as a value (the role `<select>`'s options).
 */
export type MemberRole = 'viewer' | 'editor' | 'admin';

/** The role options in least-privilege order — the order the invite form offers them in. */
export const MEMBER_ROLES: readonly MemberRole[] = ['viewer', 'editor', 'admin'];

/**
 * The roles that can write, which is what makes Requirement 6.2's and 6.5's warnings apply. Matches
 * `auth/use-can-write`'s `WRITE_ROLES`, and is fail-closed the same way: an unrecognised role reads
 * as no write access, so a *new* role would have to be added here deliberately rather than
 * inheriting silence.
 */
export function roleCanWrite(role: MemberRole | string | undefined): boolean {
  return role === 'editor' || role === 'admin';
}

/**
 * One member of the Account, as returned by `GET /v1/members` and the role-change route.
 *
 * A member is identified by their Cognito `sub` and **nothing else**: Req 2.3 keeps email and name
 * in Cognito alone, so the platform stores only the opaque identifier. That is why the members
 * table renders `userSub` verbatim rather than a display name — see the note in
 * {@link import('../pages/MembersPage').MembersPage} about what that costs the UI.
 */
export interface MemberDto {
  /** The member's Cognito `sub` — the only identifier the platform holds (Req 2.3). */
  userSub: string;
  role: MemberRole;
  /** When the membership record was created. */
  createdAtEpoch: number;
  /** The `sub` of the admin who invited them; absent for a founding member. */
  invitedBy?: string;
  /** When the invitation was accepted; absent for a membership that was not created by one. */
  acceptedAtEpoch?: number;
}

/**
 * The lifecycle of an invitation. Note there is no `expired` member: an invitation that has aged
 * past `expiresAtEpoch` is still `pending` on the wire, so expiry is derived from the timestamp
 * (Req 5.5) rather than read off the state.
 */
export type InvitationState = 'pending' | 'accepted' | 'revoked';

/** One invitation, as returned by the invitation create, list, and revoke routes (Req 5.2, 5.5). */
export interface InvitationDto {
  inviteId: string;
  /** The invited address. The one place a membership DTO carries an email rather than a `sub`. */
  email: string;
  role: MemberRole;
  /** The `sub` of the admin who sent it. */
  invitedBy: string;
  createdAtEpoch: number;
  /** After this instant the invitation is no longer acceptable (Req 5.5). */
  expiresAtEpoch: number;
  state: InvitationState;
}

/** `GET /v1/members`. */
export interface ListMembersResponse {
  members: MemberDto[];
}

/**
 * One Account the signed-in User can act as, from `GET /v1/accounts` (Req 5.8).
 *
 * `role` is the role **in that Account**, which for a shared Account comes from the membership row and
 * not from the ID token. The token's `role` is the home Account's, so the switcher must carry this
 * value into the write gates or a viewer in a shared Account would be shown controls every write then
 * refuses.
 */
export interface AccountSummary {
  accountId: string;
  role: MemberRole;
  /** Why the Account is reachable: the Cognito claim, or a membership row. Display/debug only. */
  via: 'home-claim' | 'membership';
}

/**
 * `GET /v1/accounts` — `200`. The home Account is always first and always present, even when no
 * membership row exists for it, so `accounts[0]` is the safe default selection.
 */
export interface ListAccountsResponse {
  accounts: AccountSummary[];
}

/** `POST /v1/members/invitations` — the body (Req 5.2). */
export interface CreateInvitationPayload {
  email: string;
  role: MemberRole;
}

/** `POST /v1/members/invitations` — `201`. */
export interface CreateInvitationResponse {
  invitation: InvitationDto;
}

/** `GET /v1/members/invitations`. */
export interface ListInvitationsResponse {
  invitations: InvitationDto[];
}

/** `DELETE /v1/members/invitations/{id}` — `200`. */
export interface RevokeInvitationResponse {
  revoked: true;
  id: string;
}

/** `PATCH /v1/members/{userSub}` — the body. Role is the only mutable field of a membership. */
export interface MemberRolePatch {
  role: MemberRole;
}

/** `PATCH /v1/members/{userSub}` — `200`. */
export interface UpdateMemberResponse {
  member: MemberDto;
}

/**
 * `DELETE /v1/members/{userSub}` — `200` on a clean sweep, `207` when some key revocation failed. The
 * last-admin case is refused with a `409 conflict` carrying its own message (Req 5.7), so it never
 * reaches this shape.
 *
 * The three id lists are what let the page tell the truth about a security control that is honest about
 * its limit (Req 7.8), and their presence rules differ **on purpose** — one field's absence is the
 * signal, the others' presence is:
 *
 *  - `revokedKeyIds` and `unattributedKeyIds` are **always present**, possibly empty, so a caller can
 *    tell *"none applied"* from *"not reported"*.
 *  - `failedKeyIds` is **present only when non-empty** — present-and-non-empty or absent, never `[]` —
 *    so a client cannot mistake an empty array for a fact it did not check. Its presence is the `207`,
 *    and it means the **membership is gone and some keys are not**: those ids name keys the departing
 *    member minted that are still usable.
 *
 * `unattributedKeyIds` is the residue Requirement 7.7 makes visible rather than guessing at: a key
 * recording no minter — every key issued before the platform recorded one, and every key minted by
 * another key — cannot be attributed to the departing member, so it is left alive and named for a human
 * to judge. It is not an error, and it shrinks on its own as keys expire.
 *
 * **Recovery from a `207` is per-key, not by repeating the removal.** A replayed `DELETE` answers `404`
 * for a member who no longer holds a membership row, so it writes nothing and re-attempts nothing. The
 * working recovery is `DELETE /v1/api-keys/{id}` once per reported id, which is why this body names the
 * ids rather than counting them.
 */
export interface RemoveMemberResponse {
  removed: true;
  userSub: string;
  /** Keys **this call** moved to `revoked` — the attributable ones. Always present, possibly empty. */
  revokedKeyIds: string[];
  /** Keys recording no minter, left alive and named for a human to judge (Req 7.7). Always present. */
  unattributedKeyIds: string[];
  /** Keys that should have been revoked and were not (Req 7.10). Present only when non-empty. */
  failedKeyIds?: string[];
}

/* -------------------------------------------------------------------------- */
/* Profile plane — the signed-in User's own identity                            */
/* (Feature: user-and-data-management; Requirements 3, 4, 8.1)                 */
/* -------------------------------------------------------------------------- */

/**
 * A capability the profile page must present as **explained and unavailable** rather than absent
 * (Req 3.4, 3.5, 4.5, 11).
 *
 * The reason text comes from the API rather than being written in the page, and that is deliberate: the
 * refusal `PATCH /v1/me` returns for an `email` field and the sentence beside the disabled email control
 * are the same fact, and two copies of it drift. The one a User is most likely to read is the one nobody
 * remembered to update.
 */
export interface UnavailableFeature {
  /** A stable discriminator; never displayed. */
  feature: 'email-change' | 'mfa' | 'identity-deletion';
  /** What is not available, as a heading. */
  label: string;
  /** Why, in one sentence. Rendered verbatim. */
  reason: string;
}

/**
 * The signed-in User's profile, from `GET /v1/me` and `PATCH /v1/me` (Req 3.2).
 *
 * **`name` and `email` are `string | null`, never optional.** Requirement 3.2 requires an absent value to
 * be stated explicitly rather than rendered blank, so the API returns an explicit `null` and the page
 * renders "Not set". Declaring these optional would make `undefined` a legal shape and let a
 * `?? 'Not set'` fallback hide a field the API never sent — which is a different bug wearing the same
 * clothes.
 */
export interface ProfileDto {
  /** The Cognito `sub` — the identifier the members table shows and audit records reference (Req 2.3). */
  userSub: string;
  name: string | null;
  email: string | null;
  emailVerified: boolean;
  /** Whether any MFA factor is configured. Changing it is not yet available (Req 4.5). */
  mfaEnabled: boolean;
  mfaMethods: string[];
  /** Epoch seconds, or `null` when the identity provider reported none. */
  createdAtEpoch: number | null;
  /** Every Account this User can act as, with their role in each (Req 3.2). */
  memberships: AccountSummary[];
  /**
   * That a password change does **not** revoke this User's API keys (Req 4.6). Always `false`; the page
   * states it beside the password form, because the misconception is held before the change rather than
   * after.
   */
  apiKeysRevokedOnPasswordChange: false;
  unavailable: UnavailableFeature[];
}

/** `GET /v1/me` — `200`. */
export interface GetProfileResponse {
  profile: ProfileDto;
}

/**
 * `PATCH /v1/me` — the body. **Name is the only writable field**, and the API refuses anything else with
 * a per-field `validation_failed` (Req 3.6, 3.7).
 *
 * Typed as exactly `{ name }` rather than as a partial profile, so the SPA has no way to *send* a field
 * the API would refuse. The enforcement is the API's allow-list; this is the type-level echo of it.
 */
export interface ProfileNamePatch {
  name: string;
}

/** `PATCH /v1/me` — `200`. Returns the whole profile, so the page replaces its state from one shape. */
export interface UpdateProfileResponse {
  profile: ProfileDto;
}

/** `POST /v1/me/password` — the body (Req 4.1). */
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/**
 * `POST /v1/me/password` — `200`.
 *
 * Both booleans are facts the User needs stated rather than left to infer: the session survives the
 * change (Req 4.4) and the User's API keys keep working (Req 4.6).
 */
export interface ChangePasswordResponse {
  changed: true;
  sessionRemainsValid: true;
  apiKeysRevoked: false;
  message: string;
}

/**
 * The Article 15 subject-access export — the caller's own personal data and nothing belonging to anyone
 * else (Req 8.1).
 *
 * Distinct from the Account's data export (Req 8.2, the Article 20 portability case), which answers a
 * different right and is not built. The page states which of the two this is, because handing somebody a
 * file labelled "export" that contains a third of what they expected is worse than telling them.
 */
export interface PersonalDataExport {
  generatedAt: string;
  subject: 'personal-data';
  notice: string;
  identity: {
    userSub: string;
    name: string | null;
    email: string | null;
    emailVerified: boolean;
    mfaEnabled: boolean;
    mfaMethods: string[];
    createdAtEpoch: number | null;
  };
  memberships: AccountSummary[];
  /** What the export does not cover, so its scope is not overstated. */
  notIncluded: string[];
}

/** `GET /v1/me/export` — `200`. */
export interface GetPersonalDataExportResponse {
  export: PersonalDataExport;
}

/* -------------------------------------------------------------------------- */
/* API-key plane — the Account's long-lived credentials                        */
/* (Feature: terraform-provider; Requirement 11; Design: §Components 8)        */
/* -------------------------------------------------------------------------- */

/*
 * Faithful mirrors of two shipped server-side shapes, and nothing more:
 *
 *  - the read projection `ApiKeyDto` in `services/src/identity/api-keys.ts` — an explicit seven-field
 *    allowlist, which is why there is no `accountId`, no `revokedAtEpoch`, and above all neither
 *    digest here: the server cannot send them, so the dashboard has no field to hold them; and
 *  - the four response bodies in `services/src/api/api-keys.ts` — `201 { apiKey, apiKeyValue }`,
 *    `200 { apiKeys }`, `200 { apiKey, apiKeyValue }`, and `200 { revoked: true, id }`.
 *
 * Nothing is added on the way across. A field the API does not send would be a field the page could
 * render as blank rather than as absent, and on this surface the absences are the security story.
 */

/**
 * Lifecycle state of an API_Key, exactly the union `identity/api-keys.ts` declares.
 *
 * `rotated` is part of the declared union and is **deliberately never written**: rotation keeps the
 * key `active`, because it re-keys a credential rather than retiring one. The table must still render
 * it — a value the server declares is a value the wire may carry — which is why it is mirrored here
 * rather than narrowed to the two states rotation and revocation actually produce.
 */
export type ApiKeyState = 'active' | 'rotated' | 'revoked';

/**
 * An API_Key as returned by `GET /v1/api-keys`, the create route, and the rotate route — the seven
 * fields `toApiKeyDto` projects (services Req 11.3).
 *
 * **Never the API_Key_Value, and never a hash.** The projection is an allowlist rather than a
 * `delete`-the-secrets pass, so a field added to the record later is absent here by default. The
 * plaintext value exists on exactly two responses and lives in {@link CreateApiKeyResponse}, not on
 * the key itself.
 *
 * `role` is `string`, not {@link MemberRole}, because the server's DTO widens it to `string` on the
 * way out: the record's role is graded against the authorization matrix, and a role the SPA has not
 * heard of must still render in the table rather than fail to typecheck. The *narrow* union is used
 * where it belongs — on {@link CreateApiKeyPayload}, the value the dashboard chooses.
 *
 * `lastUsedAtEpoch` is **omitted** until first use rather than sent as `null` (services Req 11.9), so
 * the table's "never" is an absence and not a sentinel — and the value is accurate only to within the
 * store's coarsening interval, which is why the page states "last used" rather than "last request".
 */
export interface ApiKeyDto {
  /** The public identifier, and the `{id}` path parameter of the rotate and revoke routes. */
  keyId: string;
  /** Operator-supplied label, so a key is recognisable in the listing before it is revoked. */
  name: string;
  /** The key's role, run through the same matrix a User's claim is. Widened to `string` server-side. */
  role: string;
  /** Issuance time, epoch **seconds** (every API_Key timestamp is seconds, not milliseconds). */
  createdAtEpoch: number;
  /** Expiry, epoch seconds — every issued key has one (services Req 11.6). */
  expiresAtEpoch: number;
  /** Last authenticated use, epoch seconds; **absent** until first use (services Req 11.9). */
  lastUsedAtEpoch?: number;
  /** Lifecycle state (services Req 11.8). */
  state: ApiKeyState;
}

/**
 * `POST /v1/api-keys` body — exactly the three fields `handleCreateApiKey` reads.
 *
 * There is no `accountId`: the Account is the credential's own and a body that named a different one
 * is refused with a `403` (services Req 8.4, 11.4). On the surface that *mints credentials* that
 * refusal matters more than anywhere else in the API, and the absent field is the dashboard's half
 * of it.
 *
 * `role` is the narrow {@link MemberRole} — the SPA's existing role union, reused rather than
 * duplicated — so the create form's `<select>` cannot offer a role the matrix does not grade. The
 * server validates it again against its own `isKnownRole`, and an unrecognised value comes back as a
 * `role` field error with nothing persisted.
 *
 * `expiresInDays` is optional and, when present, must be a **positive integer**; a
 * present-but-unusable value is a `400` rather than a silent fallback to the configured default. The
 * effective lifetime is `min(requested, configured default, configured maximum)`, so asking for more
 * than policy allows is capped silently — which is why the form's helper text names the default
 * rather than promising the request.
 */
export interface CreateApiKeyPayload {
  name: string;
  role: MemberRole;
  expiresInDays?: number;
}

/** `GET /v1/api-keys` — the Account's keys in issuance order, oldest first, in every state. */
export interface ListApiKeysResponse {
  apiKeys: ApiKeyDto[];
}

/**
 * `POST /v1/api-keys` — `201`, and `POST /v1/api-keys/{id}/rotate` — `200`. **One shape for both**,
 * because both routes answer `{ apiKey, apiKeyValue }`.
 *
 * `apiKeyValue` is the only plaintext credential the platform ever emits, and it is emitted exactly
 * once per route call: it is unrecoverable afterwards, because the record stores a digest. So it is
 * held in component state and nowhere else — never `localStorage`, never `sessionStorage`, never a
 * URL (Req 11.5). The reveal dialog is the whole reason this field is on a response type rather than
 * folded into {@link ApiKeyDto}: it belongs to the one response that produced it, not to the key.
 */
export interface CreateApiKeyResponse {
  apiKey: ApiKeyDto;
  apiKeyValue: string;
}

/**
 * `DELETE /v1/api-keys/{id}` — `200 { revoked: true, id }`.
 *
 * A state transition, not a delete: the key stays listed and auditable with its digests intact,
 * because the authenticator checks `state` before it compares any hash. The route is idempotent, so
 * revoking an already-revoked key answers this same shape rather than a `409` — the `409` on this
 * surface belongs to *rotating* a revoked key, which would resurrect it.
 */
export interface RevokeApiKeyResponse {
  revoked: true;
  id: string;
}

/* -------------------------------------------------------------------------- */
/* Check payload defaults, mirrored rather than imported                        */
/* -------------------------------------------------------------------------- */

/**
 * The four Check defaults the platform applies when a payload omits them — the same values as
 * `@soroush/core`'s `CHECK_DEFAULTS`, **mirrored here rather than imported**.
 *
 * ## Why a copy, when core exports the real thing
 *
 * `@soroush/core` is emitted as CommonJS (`tsconfig.base.json` sets `"module": "commonjs"`), so its
 * `dist/index.js` re-exports every submodule through `__exportStar(require("./domain/defaults"))`.
 * Rollup cannot statically resolve a named export through a CJS star re-export, so importing a
 * **value** from `@soroush/core` type-checks and passes `vitest` but fails `vite build` with
 * *"CHECK_DEFAULTS is not exported by ../core/dist/index.js"*. Every other `@soroush/core` import in
 * this package is an `import type`, which is erased before Rollup sees it — which is why the SPA
 * bundle has never contained a line of core's runtime code and why this only surfaced the first time
 * one was added.
 *
 * That is a build constraint, but the rule it enforces is a deliberate one: the SPA bundle takes
 * **types** from the backend packages and never values. `lib/aggregation.ts` already follows it,
 * reimplementing the backend's roll-up arithmetic over the read DTOs rather than importing it, and
 * pinning the equivalence in a test instead.
 *
 * So this constant is the same pattern: a copy, with the drift guard in
 * `types.check-defaults.test.ts`, which *does* import core's `CHECK_DEFAULTS` and asserts deep
 * equality. Test files are never bundled, so the guard costs the browser nothing and fails loudly
 * the moment the two disagree.
 */
export const CHECK_PAYLOAD_DEFAULTS = {
  /** Consecutive failing results that mark a Check down. */
  failureThreshold: 1,
  /** Retries on transient infrastructure errors before a result is recorded. */
  maxRetries: 2,
  /** No Warm_Pool unless a Check is explicitly latency-sensitive. */
  latencySensitive: false,
  /** Checks are enabled unless explicitly disabled. */
  enabled: true,
} as const;

/* -------------------------------------------------------------------------- */
/* Inference plane — the Account's Inference_Configuration                      */
/* (Feature: customer-managed-inference, task 17.1; Requirements 1.5, 1.6,     */
/*  3.1, 5.4–5.8, 7.3, 10.2, 13.2, 13.4; Design: §Components 3, 22)            */
/* -------------------------------------------------------------------------- */

/*
 * Hand-written mirrors of the five server shapes this plane returns:
 *
 *  - `InferenceConfiguration`'s read projection `InferenceConfigurationDto`
 *    (`services/src/inference/configuration.ts`),
 *  - the typed `InferenceFailure` (`services/src/inference/failure.ts`),
 *  - the `PublishedRolePolicy` and its IAM documents (`services/src/inference/role-policy.ts`),
 *  - the four `Inference_Mode` / `Source_Type` vocabularies (`services/src/inference/mode.ts`), and
 *  - the five response envelopes (`services/src/api/inference-config.ts`).
 *
 * **Mirrored rather than imported, and the constraint is the same one {@link CHECK_PAYLOAD_DEFAULTS}
 * documents at length.** `@soroush/services` is not a dependency of this package at all, and
 * `@soroush/core` is emitted as CommonJS, so a **value** imported from either type-checks, passes
 * `vitest`, and then fails `vite build` at the Rollup step. `INFERENCE_MODES` and `SOURCE_TYPES` are
 * needed as values here — they are the `<select>`'s options and the pin editor's row order — so they
 * are declared, not re-exported. The `import type` above is erased before Rollup sees it, which is why
 * {@link Modality} may be borrowed while these two may not.
 *
 * Nothing is added on the way across, and one thing is deliberately **not** mirrored: the server's
 * `InferenceConfigurationErrorDto` — the read DTO minus the External_Id, which every `4xx` on this
 * surface carries (Requirement 2.9). The dashboard reads a refusal through {@link ApiError}, whose
 * message and `fieldErrors` are all the page renders from one, and it re-reads the configuration on
 * success only. A type for the error body would invite a page to render a *failed* write's
 * configuration as though it were the stored one, which is exactly the optimism Requirement 7.7 rules
 * out.
 */

/**
 * Where an Account's model invocations execute (Requirement 1.1) — exactly two values, mirroring
 * `services/src/inference/mode.ts`.
 *
 * There is no `disabled` and no `pending`: a configuration that is not usable yet is a
 * `customer_hosted` mode whose {@link VerificationState} is not `verified`, which keeps "where does
 * inference run" and "may it run yet" as two independent facts.
 */
export type InferenceMode = 'soroush_hosted' | 'customer_hosted';

/** Both {@link InferenceMode} values, in the order the API and this page present them. */
export const INFERENCE_MODES: readonly InferenceMode[] = ['soroush_hosted', 'customer_hosted'];

/**
 * Whether the platform has confirmed it can reach this Account's configured models (Requirement 1.5).
 *
 * `verified` is set only when role assumption **and** every selected Inference_Profile's capability
 * probe succeeded; `failed` says the configuration itself is no longer valid, which is why the page
 * must state the fault rather than appear healthy (Requirement 7.7).
 */
export type VerificationState = 'unverified' | 'verified' | 'failed';

/**
 * What kind of document a Generation_Request's Source_Document turned out to be, and therefore which
 * Model_Pin applies (Requirement 6.1).
 *
 * Four values and no fifth for "no document": a request with no Source_Document resolves through the
 * configuration's defaults (Requirement 6.3), so the fallback is an absence rather than a member.
 */
export type SourceType = 'openapi' | 'markdown' | 'html' | 'text';

/** Every {@link SourceType}, in the order the pin editor lists them. */
export const SOURCE_TYPES: readonly SourceType[] = ['openapi', 'markdown', 'html', 'text'];

/**
 * The Account's default Inference_Profile pair: the Primary_Model every first attempt targets and the
 * Escalation_Model every Self_Correction attempt targets. **Both required together** — a pair with one
 * member resolves an escalation to nothing at the moment it is needed.
 */
export interface ProfileSelection {
  primary: string;
  escalation: string;
}

/**
 * A per-Source_Type override (Requirements 6.2, 6.4). Each tier falls back **independently**
 * (Requirement 6.3), which is why both members are optional rather than the pin being all-or-nothing.
 */
export interface ModelPin {
  primary?: string;
  escalation?: string;
}

/** Model_Pins by Source_Type. Partial: an absent Source_Type falls back to the defaults. */
export type ModelPins = Partial<Record<SourceType, ModelPin>>;

/**
 * An administrator's explicit acknowledgement that a `global.`-prefixed Inference_Profile may execute
 * outside the Customer_Inference_Region's geography (Requirements 5.4, 5.6).
 *
 * **Keyed on {@link profileId}, and the page's acknowledgement control is keyed the same way**: the
 * acknowledgement covers the identifier it was given for and no other, so changing the identifier
 * leaves the new one unacknowledged (Requirement 5.7). `actor` and `atEpoch` are stamped from the
 * authenticated credential and the platform's clock, never from a request body — which is why
 * {@link InferenceConfigurationPayload} sends `{ profileId }` alone.
 */
export interface GeographyAcknowledgement {
  profileId: string;
  actor: ActorRefDto;
  atEpoch: number;
}

/**
 * What the Model_Capability_Probe found for one Inference_Profile (Requirements 4.5, 4.9).
 *
 * `stopReason` is **recorded and never a rejection criterion**: a model may legitimately report
 * `end_turn` alongside a well-formed tool call. `modalities` is the literal `'unknown'` whenever the
 * platform has no row for the underlying foundation model — a recorded fact, never a refusal.
 */
export interface ProbeOutcome {
  profileId: string;
  result: 'passed' | 'failed';
  stopReason?: string;
  modalities: Modality[] | 'unknown';
  failure?: InferenceFailureKind;
  atEpoch: number;
}

/**
 * Which stage of resolution failed — eleven arms, closed, mirroring
 * `services/src/inference/failure.ts`.
 *
 * The page renders a remediation sentence per arm, naming what the customer changes in **their own**
 * account (Requirement 7.4). `probe_throttled` is the one arm that is not a fault: the probe was
 * throttled, so the configuration is *unjudged* rather than broken (Requirement 10.5).
 */
export type InferenceFailureKind =
  | 'not_verified'
  | 'role_assumption_refused'
  | 'external_id_rejected'
  | 'region_unusable'
  | 'profile_unavailable'
  | 'access_denied'
  | 'capability_unsupported'
  | 'probe_throttled'
  | 'probe_failed'
  | 'refused_model_resource'
  | 'geography_unacknowledged';

/**
 * A typed inference failure: which stage failed, plus the three identifiers Requirement 7.3 asks it to
 * name so an administrator knows what to fix.
 *
 * **There is deliberately no `message` field, and there is no External_Id.** `errorClass` is the
 * provider's exception *class* and never its message (Requirement 7.9), because STS and Bedrock
 * messages routinely quote the request that provoked them — and here that request carries the
 * customer's own API documentation. The External_Id is excluded from every error body for the opposite
 * reason (Requirement 7.6). Both absences are what let the page render a failure verbatim.
 */
export interface InferenceFailureDto {
  kind: InferenceFailureKind;
  /** The Customer_Inference_Role the attempt targeted. */
  customerRoleArn?: string;
  /** The Customer_Inference_Region the attempt targeted. */
  region?: string;
  /** The Inference_Profile identifier concerned. */
  inferenceProfileId?: string;
  /** The provider exception **class**, never its message (Requirement 7.9). */
  errorClass?: string;
}

/**
 * The spend guards in force for the Account (Requirement 10.2).
 *
 * **Displayed, never set.** Requirement 10.2 asks that the limits protecting a customer's bill be
 * theirs to *see*; making them theirs to *set* is **OI-D2** and out of scope, so there is no payload
 * field for them and the page renders no control that would change one. They apply in **both** modes
 * (Requirement 10.1): a customer paying for their own inference is still bounded by the platform's
 * rate, concurrency, and token caps.
 */
export interface EffectiveInferenceLimits {
  /** Generation_Requests per rolling hour, per Account. */
  ratePerHour: number;
  /** Concurrent in-progress Generation_Requests, per Account. */
  maxConcurrent: number;
  /** Configured input-token cap; the *effective* cap also honours the profile's context window. */
  maxInputTokens: number;
  /** Output-token cap per attempt. */
  maxOutputTokens: number;
}

/**
 * The `GET /v1/inference-configuration` read model (Requirements 1.5, 1.6, 10.2, 13.2).
 *
 * `externalId` is returned **unmasked**, deliberately (Requirement 1.6): it is an anti-confused-deputy
 * nonce rather than a credential — possession alone reaches nothing, because an attacker also has to
 * *be* the Inference_Principal — and an administrator cannot write the `sts:ExternalId` condition
 * without reading it. That is why the page shows it with a copy control and not behind a mask.
 *
 * `geographyAcknowledgements` and `probes` are **always arrays**, empty when there are none: the
 * server declines the absent-versus-empty distinction, so no reader needs a `?? []`.
 */
export interface InferenceConfigurationDto {
  /**
   * The Account this configuration belongs to — the **authenticated credential's**, joined onto the
   * response by the server and never sent by a caller.
   *
   * Required, like the server's member: every read on this surface is account-scoped by construction.
   * On the read because the Customer_Inference_Role must be named `soroush-inference-<accountId>`, so
   * neither the role-policy page nor the Terraform module of Requirement 3.6 can compose the name
   * without it. Nothing in {@link InferenceConfigurationPayload} mirrors it: a body naming `accountId`
   * is refused (Requirements 1.4, 2.2), so this is read-only in the strongest sense the API has.
   */
  accountId: string;
  mode: InferenceMode;
  verificationState: VerificationState;
  /** Deliberately unmasked (Requirement 1.6) — see the interface doc. */
  externalId: string;
  changedAtEpoch: number;
  /** Who made the most recent change; **absent** on an Account that has never configured anything. */
  changedBy?: ActorRefDto;
  /** Retained across a switch back to `soroush_hosted` (Requirement 9.6). */
  customerRoleArn?: string;
  /** Retained across a switch back to `soroush_hosted` (Requirement 9.6). */
  customerRegion?: string;
  /** Retained across a switch back to `soroush_hosted` (Requirement 9.6). */
  profiles?: ProfileSelection;
  /** Retained across a switch back to `soroush_hosted` (Requirement 9.6). */
  pins?: ModelPins;
  /** Always present, empty when none. */
  geographyAcknowledgements: GeographyAcknowledgement[];
  /** Always present, empty when the configuration has never been probed. */
  probes: ProbeOutcome[];
  /** Set when the configuration itself is no longer valid (Requirement 7.7). */
  failure?: InferenceFailureDto;
  /** Requirement 10.2 — displayed, never set. */
  effectiveLimits: EffectiveInferenceLimits;
  /** The one principal every Customer_Inference_Role admits (Requirements 2.6, 3.1, 3.5). */
  inferencePrincipalArn: string;
}

/** The `sts:ExternalId` condition on a trust policy (Requirement 2.4). */
export interface IamConditionDto {
  StringEquals: Record<string, string>;
}

/**
 * One statement of a Published_Role_Policy document. `Principal` and `Condition` appear on the trust
 * policy only; `Resource` on the permission policy only.
 */
export interface IamStatementDto {
  Effect: 'Allow';
  Action: string | string[];
  /** A single `AWS` principal — never a list (Requirement 3.5). */
  Principal?: { AWS: string };
  Resource?: string[];
  Condition?: IamConditionDto;
}

/** An IAM policy document, at the one `Version` AWS has accepted since 2012. */
export interface IamPolicyDocumentDto {
  Version: '2012-10-17';
  Statement: IamStatementDto[];
}

/**
 * `GET /v1/inference-configuration/role-policy`'s payload — the role to create, the two policies to
 * put on it, and the Terraform module that does both (Requirements 3.1, 3.6, 3.7).
 *
 * `advisory` is present **only** when a selection or the region moved since the last passing probe, so
 * its presence means something: the permission policy on the customer's role must be updated *before*
 * verifying, because the new ARNs are not allowed yet.
 */
export interface PublishedRolePolicyDto {
  /** `soroush-inference-<accountId>`, the name the Inference_Principal's own policy admits. */
  roleName: string;
  /** Who may assume the role, and under which External_Id (Requirements 2.4, 3.5). */
  trustPolicy: IamPolicyDocumentDto;
  /** What the role may do once assumed (Requirements 3.2, 3.3, 3.4, 3.8). */
  permissionPolicy: IamPolicyDocumentDto;
  /** The exact `terraform` to apply, for the page's copy control (Requirement 3.6). */
  terraformModule: string;
  /** Present only when the role must be updated before verifying (Requirement 3.7). */
  advisory?: string;
}

/**
 * The `PUT /v1/inference-configuration` entity — exactly the six fields an administrator may set.
 *
 * Everything absent here is server-managed by **construction**: `verificationState`, `probes`,
 * `changedAtEpoch`, `changedBy`, and `failure` have no field to occupy. `externalId` and `accountId`
 * are the two the server refuses *explicitly* rather than ignoring, because a caller naming either has
 * a misconception worth correcting (Requirements 1.4, 2.2) — so this type has neither, and the SPA has
 * no way to send one.
 *
 * A `customer_hosted` payload must carry the role ARN, the region, and the profile pair; a
 * `soroush_hosted` one may carry nothing but the mode, and the server retains the stored customer
 * fields (Requirement 9.6). The optionality is the type-level echo of that split.
 */
export interface InferenceConfigurationPayload {
  mode: InferenceMode;
  customerRoleArn?: string;
  customerRegion?: string;
  profiles?: ProfileSelection;
  pins?: ModelPins;
  /**
   * `{ profileId }` per acknowledged identifier, and nothing else — the shape the read returns, so a
   * read-edit-write round trip needs no reshaping. An `actor` or `atEpoch` here is simply not read.
   */
  geographyAcknowledgements?: { profileId: string }[];
}

/**
 * `PUT /v1/inference-configuration`'s body. The entity is nested under `inferenceConfiguration`, which
 * is also the root of every {@link FieldError} `path` the route returns — `inferenceConfiguration.
 * profiles.primary`, `inferenceConfiguration.customerRoleArn`, `inferenceConfiguration.pins.openapi.
 * primary`. The page places its errors on those paths verbatim, so one error-mapping path serves the
 * SPA, the CLI, and the Terraform provider.
 */
export interface ReplaceInferenceConfigurationPayload {
  inferenceConfiguration: InferenceConfigurationPayload;
}

/** `GET /v1/inference-configuration` — `200 { inferenceConfiguration }`. */
export interface GetInferenceConfigurationResponse {
  inferenceConfiguration: InferenceConfigurationDto;
}

/**
 * `PUT /v1/inference-configuration` — `200 { inferenceConfiguration }` when the record now describes
 * what the caller asked for.
 *
 * A `customer_hosted` target that could not be verified is a **`409`**, not this shape: the mode stays
 * where it was and the body says why (Requirement 9.2). It reaches the page as an {@link ApiError}.
 */
export interface ReplaceInferenceConfigurationResponse {
  inferenceConfiguration: InferenceConfigurationDto;
}

/**
 * `POST /v1/inference-configuration/rotate-external-id` — `200 { inferenceConfiguration, advisory }`
 * (Requirement 2.8).
 *
 * The `advisory` travels with the response rather than living in this page's copy, on the
 * `ROLE_UPDATE_ADVISORY` precedent: the SPA and the CLI render the same string, so neither can omit it
 * and the two cannot word it differently. It says what rotation costs — the trust policy must name the
 * new value before customer-hosted generation can resume.
 */
export interface RotateInferenceExternalIdResponse {
  inferenceConfiguration: InferenceConfigurationDto;
  advisory: string;
}

/**
 * `POST /v1/inference-configuration/verify` — `200 { inferenceConfiguration }` when every probe
 * passed, otherwise a `409` carrying the failure. Never changes the Inference_Mode; only `PUT` does.
 */
export interface VerifyInferenceConfigurationResponse {
  inferenceConfiguration: InferenceConfigurationDto;
}

/**
 * `GET /v1/inference-configuration/role-policy` — `200 { rolePolicy }`.
 *
 * A `409` before any customer configuration is stored, because the permission policy's `Resource` list
 * is derived from the region and the selections and AWS rejects an IAM document with an empty
 * `Resource`. The two values a customer needs *first* — the External_Id and the Inference_Principal
 * ARN, which together are the whole trust policy — are on the configuration read, so the setup
 * ordering is never blocked by that refusal.
 */
export interface GetInferenceRolePolicyResponse {
  rolePolicy: PublishedRolePolicyDto;
}
